import { ScoredDrawResult } from '@API/game-results/types'
import {
  AddonResultDetails,
  ListResult,
  ListViewMatchDetails,
  MatchColorConfig,
} from '../list-view/list-view'
import { MatchStatus } from '../number-selection-summary/number-selection-summary-types'
import { HostConfig } from '@AppState/host-config'
import {
  ExtraDrawsAddOnSelection,
  MultiplierAddOnSelection,
  MiniGameAddOnSelection,
  TicketInfo,
} from '@Config/ticket-form-types'
import { SavedKenoTicket } from '@interaction-gaming/player-data-service-browser-client'
import { AddOnTypes } from '@Config/types'

export const mapListResults = (
  scoredDrawResults: ScoredDrawResult[],
  numberSelections: number[],
  extraDrawAddOnSelectionName: string | undefined,
  hostConfig: HostConfig,
) => {
  return scoredDrawResults.map((res) => {
    if (!res.drawingComplete) {
      const details: ListViewMatchDetails[] = numberSelections.map((sel) => {
        return {
          number: sel,
          matchStatus: MatchStatus.UNKNOWN,
          addOnName: null,
          colorOverride: null,
        }
      })

      const result: ListResult = {
        type: 'PENDING',
        drawNumber: res.drawingNumber,
        drawDate: null,
        numberSelections: details,
        totalWinAmountInCents: 0,
        scoringBreakdown: null,
        extraDrawAddOnResult: undefined,
        miniGameAddOnResult: undefined,
        multiplierAddOnResult: undefined,
        iconUrl:
          hostConfig.icons.pendingUrls && hostConfig.icons.pendingUrls.length
            ? hostConfig.icons.pendingUrls[res.drawingNumber % hostConfig.icons.pendingUrls.length]
            : null,
      }
      return result
    }

    const extraDrawAddon = res.extraDrawAddOnResult

    const details: ListViewMatchDetails[] = numberSelections.map((sel) => {
      const isMatched =
        res.winningNumbers.find((result) => result.numberSelection === sel) !== undefined

      const addonMatch =
        extraDrawAddon?.results &&
        extraDrawAddon.name === extraDrawAddOnSelectionName &&
        extraDrawAddon.results.find((result) => result.result === sel) !== undefined

      const result: ListViewMatchDetails = {
        number: sel,
        matchStatus: isMatched || addonMatch ? MatchStatus.MATCH : MatchStatus.UNMATCHED,
        addOnName: (addonMatch && extraDrawAddon?.name) || null,
      }

      return result
    })

    const resultUrls =
      res.totalWinningsInCents > 0 ? hostConfig.icons.winningUrls : hostConfig.icons.nonWinUrls

    const result: ListResult = {
      type: 'COMPLETED',
      drawNumber: res.drawingNumber,
      drawDate: res.drawingDate,
      totalWinAmountInCents: res.totalWinningsInCents,
      numberSelections: details,
      scoringBreakdown: res.scoringBreakdown,
      extraDrawAddOnResult: res.extraDrawAddOnResult,
      miniGameAddOnResult: res.miniGameAddOnResult,
      multiplierAddOnResult: res.multiplierAddOnResult,
      iconUrl:
        resultUrls && resultUrls.length ? resultUrls[res.drawingNumber % resultUrls.length] : null,
    }
    return result
  })
}

export const mapAddonDetails = (
  multiplierAddOnSelection: Partial<MultiplierAddOnSelection> | undefined,
  extraDrawAddOnSelection: Partial<ExtraDrawsAddOnSelection> | undefined,
  miniGameAddOnSelection: Partial<MiniGameAddOnSelection> | undefined,
  matchColors: MatchColorConfig[],
): AddonResultDetails[] => {
  const addOns = []

  if (multiplierAddOnSelection && multiplierAddOnSelection.name) {
    addOns.push({
      name: multiplierAddOnSelection.name,
      color: null,
      type: AddOnTypes.MULTIPLIER,
    })
  }

  if (extraDrawAddOnSelection && extraDrawAddOnSelection.name) {
    const addonColor = matchColors.find((val) => val.name === extraDrawAddOnSelection.name)?.color

    addOns.push({
      name: extraDrawAddOnSelection.name,
      color: addonColor ? addonColor : null,
      type: AddOnTypes.EXTRA_DRAWS,
    })
  }

  if (miniGameAddOnSelection && miniGameAddOnSelection.name) {
    addOns.push({
      name: miniGameAddOnSelection.name,
      color: null,
      type: AddOnTypes.MINI_GAME,
    })
  }

  return addOns
}

export const mapTicketToTicketInfoForm = (savedKenoTicket: SavedKenoTicket, newTicket: boolean = false, latestDraw: number | null): TicketInfo => {
  return {
    id: newTicket ? undefined : savedKenoTicket.id,
    startingDrawNumber: newTicket ? (latestDraw ?? 1) : savedKenoTicket.drawingRange.min,
    numberOfDraws: savedKenoTicket.drawingRange.max - savedKenoTicket.drawingRange.min + 1,
    baseWager: savedKenoTicket.wagerInCents,
    numberSelections: savedKenoTicket.numberSelections,
    multiplierAddOnSelection: savedKenoTicket.addonSelection.multiplierAddon
      ? { name: savedKenoTicket.addonSelection.multiplierAddon, type: AddOnTypes.MULTIPLIER }
      : undefined,
    miniGameAddOnSelection: savedKenoTicket.addonSelection.miniGameAddon
      ? {
          name: savedKenoTicket.addonSelection.miniGameAddon.name,
          type: AddOnTypes.MINI_GAME,
          numberSelections: savedKenoTicket.addonSelection.miniGameAddon.numberSelections,
        }
      : undefined,
    extraDrawAddOnSelection: savedKenoTicket.addonSelection.extraDrawAddon
      ? { name: savedKenoTicket.addonSelection.extraDrawAddon, type: AddOnTypes.EXTRA_DRAWS }
      : undefined,
  }
}
